import api from './api';
import { DetailsInterface } from '../containers/EventsPage/types';
import { EventsSearchObject } from '../common/types';
import { isFreeTextObject } from '../common/utils/eventsSearchUtils';
import { SelectedEventDetails } from '../containers/EventsPage/types';
import { uniqBy } from 'lodash';
import { EVENTS_PAGE_LIMIT } from '@/containers/EventsPage/constants';

export enum EventsContextType {
  Succeeding = 'succeeding',
  Preceding = 'preceding',
  Context = 'fullContext',
}

// Types for the API responses
export type EventsResponse = {
  events: DetailsInterface[];
  totalCount: number;
  currentPage: number;
  start?: string;
  end?: string;
  hasMoreSucceeding?: boolean;
  hasMorePreceding?: boolean;
};

export type EventDetailsResponse = Record<string, any>;

// Query parameters for getEventsForEntity
export type GetEventsParams = {
  entityId: string;
  startDate?: string;
  endDate?: string;
  levels?: string[];
  searchObject?: EventsSearchObject;
  offset?: number;
  limit?: number;
};

// Query parameters for getSelectedEventDetails
export type GetSelectedEventDetailsParams = {
  entityId: string;
  eventId: string;
};

type GetMoreEventsParams = {
  entityId: string;
  type: EventsContextType;
  searchObject?: EventsSearchObject;
  eventId?: string;
  levels?: string[];
  limit?: number;
  offset?: number;
};

export const entityEventsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getEventsForEntity: builder.query<EventsResponse, GetEventsParams>({
      query: ({
        entityId,
        startDate,
        endDate,
        levels = [],
        searchObject,
        offset = 0,
        limit = EVENTS_PAGE_LIMIT,
      }) => {
        let url = `/events?entityId=${entityId}`;

        if (levels.length > 0) {
          levels.forEach((level) => {
            url += `&levels=${level}`;
          });

          if (levels.length === 6) {
            url += '&levels=SHOW ALL';
          }
        }

        if (startDate) {
          url += `&fromDate=${startDate}`;
        }
        if (endDate) {
          url += `&toDate=${endDate}`;
        }

        url += `&offset=${offset}&limit=${limit}`;

        if (searchObject) {
          if (isFreeTextObject(searchObject)) {
            Object.values(searchObject).forEach((values) => {
              values.forEach((v) => {
                url += `&freetext=${encodeURIComponent(v)}`;
              });
            });
          } else {
            Object.entries(searchObject).forEach(([key, values]) => {
              values.forEach((v) => {
                url += `&${key}=${encodeURIComponent(v)}`;
              });
            });
          }
        }

        return {
          url,
          method: 'GET',
        };
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,

      transformResponse: (response: EventsResponse, meta, args) => {
        const latestOffset = args.offset || 0;
        const currentPage = Math.floor(latestOffset / EVENTS_PAGE_LIMIT);

        return {
          ...response,
          currentPage,
          hasMoreSucceeding: true,
          hasMorePreceding: true,
        };
      },
      providesTags: ['EntityEvents'],
    }),

    getSelectedEventDetails: builder.query<
      SelectedEventDetails,
      GetSelectedEventDetailsParams
    >({
      query: ({ entityId, eventId }) => ({
        url: `/events/details?entityId=${entityId}&eventId=${eventId}`,
        method: 'GET',
      }),
      providesTags: ['EntityEventDetails'],
    }),

    getMoreEvents: builder.query<DetailsInterface[], GetMoreEventsParams>({
      query: ({
        entityId,
        eventId,
        searchObject,
        levels = [],
        limit,
        type,
      }) => {
        let url = `/events/context?entityId=${entityId}`;

        if (levels.length > 0) {
          levels.forEach((level) => {
            url += `&levels=${level}`;
          });

          if (levels.length === 6) {
            url += '&levels=SHOW ALL';
          }
        }

        url += `&limit=${limit}&eventsTypeOrder=${type}`;

        if (eventId) {
          url += `&eventId=${eventId}`;
        }

        if (searchObject) {
          if (isFreeTextObject(searchObject)) {
            Object.values(searchObject).forEach((values) => {
              values.forEach((v) => {
                url += `&freetext=${encodeURIComponent(v)}`;
              });
            });
          } else {
            Object.entries(searchObject).forEach(([key, values]) => {
              values.forEach((v) => {
                url += `&${key}=${encodeURIComponent(v)}`;
              });
            });
          }
        }

        return {
          url,
          method: 'GET',
        };
      },

      // Manually pushes the response from getMoreEvents to the main cache of getEventsForEntity
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { type } = arg;

        try {
          const { data: contextEvents } = await queryFulfilled;

          dispatch(
            entityEventsApi.util.updateQueryData(
              'getEventsForEntity',
              {} as GetEventsParams,
              (draft) => {
                switch (type) {
                  case EventsContextType.Succeeding: {
                    if (contextEvents.length < 10) {
                      draft.hasMoreSucceeding = false;
                    }

                    const totalEvents = uniqBy(
                      [...contextEvents, ...draft.events],
                      'id',
                    );
                    const paginatedEvents = totalEvents.slice(
                      0,
                      EVENTS_PAGE_LIMIT,
                    );

                    draft.events = paginatedEvents;
                    draft.totalCount = draft.totalCount + contextEvents.length;

                    break;
                  }

                  case EventsContextType.Preceding: {
                    if (contextEvents.length < 10) {
                      draft.hasMorePreceding = false;
                    }

                    const totalEvents = uniqBy(
                      [...draft.events, ...contextEvents],
                      'id',
                    );

                    const paginatedEvents = totalEvents.slice(
                      0,
                      EVENTS_PAGE_LIMIT,
                    );

                    draft.events = paginatedEvents;
                    draft.totalCount = draft.totalCount + contextEvents.length;

                    break;
                  }

                  case EventsContextType.Context:
                    draft.events = contextEvents;
                    draft.totalCount = contextEvents.length;
                    draft.hasMoreSucceeding = true;
                    draft.hasMorePreceding = true;

                    break;

                  default:
                    break;
                }
              },
            ),
          );
        } catch {
          // Handle errors
        }
      },
    }),
  }),
});

export const {
  useGetEventsForEntityQuery,
  useLazyGetEventsForEntityQuery,
  useLazyGetMoreEventsQuery,
  useGetSelectedEventDetailsQuery,
} = entityEventsApi;
